import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { toast } from 'sonner';

const AttestationModal = ({ onAccept }) => {
    const { logout } = useAuth();
    const [loading, setLoading] = useState(false);

    const handleAccept = async () => {
        setLoading(true);
        try {
            const response = await authAPI.acceptAttestation();
            if (response.data.success) {
                toast.success('Welcome to ClientCheck!');
                onAccept(response.data.user);
            }
        } catch (error) {
            toast.error('Failed to record attestation. Please try again.');
            console.error('Attestation error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        window.location.href = '/login';
    };

    return (
        <div 
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
            data-testid="attestation-modal-overlay"
        >
            <div 
                className="bg-card border border-border rounded-xl shadow-2xl max-w-lg w-full mx-4 p-6 md:p-8"
                data-testid="attestation-modal"
            >
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <svg 
                            className="w-6 h-6 text-primary" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                        >
                            <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" 
                            />
                        </svg>
                    </div>
                    <h2 
                        className="text-xl md:text-2xl font-bold text-foreground"
                        data-testid="attestation-modal-title"
                    >
                        Confirm your intended use
                    </h2>
                </div>

                {/* Body */}
                <div className="mb-8">
                    <p 
                        className="text-muted-foreground leading-relaxed"
                        data-testid="attestation-modal-body"
                    >
                        ClientCheck is a contractor-to-contractor job experience sharing network. 
                        By continuing, you confirm that you are accessing this platform solely as 
                        a trade contractor to share or review job experiences. You confirm you will 
                        <span className="font-semibold text-foreground"> NOT </span>
                        use this platform for tenant screening, employment decisions, insurance 
                        underwriting, or any other purpose prohibited by our{' '}
                        <a 
                            href="/terms" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                        >
                            Terms of Service
                        </a>.
                    </p>
                </div>

                {/* Buttons */}
                <div className="flex flex-col gap-3">
                    <button
                        onClick={handleAccept}
                        disabled={loading}
                        className="w-full py-3 px-4 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        data-testid="attestation-confirm-btn"
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                    <circle 
                                        className="opacity-25" 
                                        cx="12" 
                                        cy="12" 
                                        r="10" 
                                        stroke="currentColor" 
                                        strokeWidth="4"
                                        fill="none"
                                    />
                                    <path 
                                        className="opacity-75" 
                                        fill="currentColor" 
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                </svg>
                                Processing...
                            </>
                        ) : (
                            <>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                I confirm — continue to dashboard
                            </>
                        )}
                    </button>
                    
                    <button
                        onClick={handleLogout}
                        disabled={loading}
                        className="w-full py-3 px-4 bg-transparent border border-border text-muted-foreground font-medium rounded-lg hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="attestation-logout-btn"
                    >
                        Log out
                    </button>
                </div>

                {/* Footer note */}
                <p className="mt-6 text-xs text-muted-foreground text-center">
                    Your acceptance is recorded with timestamp and IP address for compliance purposes.
                </p>
            </div>
        </div>
    );
};

export default AttestationModal;
